import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { animated } from 'react-spring'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'
import { BlockItemShape } from '../propTypes'
import { useVisualBlocksContext } from '../VisualBlocksContext'
import { getAllocationString, getFormatString } from '../utils'

const BlockItemHoverDetailItem = ({
  item,
  allocation,
  isLastItem,
  depth,
  onHoverChange
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const { formatter } = useFormattingContext()
  const { formats } = useVisualBlocksContext()
  const formatString = getFormatString(formats, depth)

  useEffect(() => {
    if (isHovered) {
      onHoverChange?.(item.id)
    }
  }, [onHoverChange, item.id, isHovered])

  return (
    <animated.div
      className={clsx('flex flex-1 items-end justify-end p-2', {
        'text-white font-bold': isHovered
      })}
      style={{
        flexBasis: allocation,
        borderRight: !isLastItem ? '4px solid rgba(0, 0, 0, 0.4)' : 'none',
        backgroundColor: isHovered ? 'rgba(0, 0, 0, 0.4)' : 'transparent'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div>{formatter(item.value, formatString)}</div>
    </animated.div>
  )
}
BlockItemHoverDetailItem.propTypes = {
  allocation: PropTypes.string.isRequired,
  isLastItem: PropTypes.bool.isRequired,
  depth: PropTypes.number,
  item: BlockItemShape,
  onHoverChange: PropTypes.func
}

export default function BlockItemHoverDetails ({
  items,
  depth,
  visible,
  minAllocationToShow = 0.03,
  onHoverChange
}) {
  const totalValue = useMemo(
    () => items.reduce((sum, item) => sum + item.value, 0),
    [items]
  )

  // group items under threshold into "Other"
  const normalizedItems = useMemo(() => {
    if (!items?.length) return []
    const aboveThreshold = items.filter(
      (item) => item.value / totalValue >= minAllocationToShow
    )
    const belowThreshold = items.filter(
      (item) => item.value / totalValue < minAllocationToShow
    )
    if (belowThreshold.length === 0) return aboveThreshold
    const otherValue = belowThreshold.reduce((sum, item) => sum + item.value, 0)
    return [
      ...aboveThreshold,
      {
        id: '_remaining_' + belowThreshold.map(({ id }) => id).join(','),
        label: 'Other',
        value: otherValue
      }
    ]
  }, [items, minAllocationToShow, totalValue])

  return (
    <div
      className='w-full h-full'
      style={{ display: visible ? 'flex' : 'none' }}
    >
      {normalizedItems.map((item, i) => (
        <BlockItemHoverDetailItem
          key={item.label}
          isLastItem={i >= normalizedItems.length - 1}
          allocation={getAllocationString(item.value, totalValue)}
          item={item}
          onHoverChange={onHoverChange}
          depth={depth}
        />
      ))}
    </div>
  )
}

BlockItemHoverDetails.propTypes = {
  items: PropTypes.arrayOf(BlockItemShape).isRequired,
  depth: PropTypes.number.isRequired,
  visible: PropTypes.bool,
  minAllocationToShow: PropTypes.number,
  onHoverChange: PropTypes.func
}
